// SPDX-FileCopyrightText: Copyright 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CloseIcon,
  DeleteIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import { Alert, Button, InlineSpinner, Tooltip } from "@vector-im/compound-web";
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";

import {
  finishOauth2Session,
  oauth2ClientQuery,
  oauth2SessionQuery,
  userQuery,
} from "@/api/mas";
import * as Data from "@/components/data";
import { DetailHeader } from "@/components/detail-header";
import * as Dialog from "@/components/dialog";
import { Disclosure } from "@/components/disclosure";
import { StaticEntityCard } from "@/components/entity-card";
import { ButtonLink } from "@/components/link";
import * as Navigation from "@/components/navigation";
import * as messages from "@/messages";
import {
  DeviceInfo,
  DeviceTypeHero,
  useDeviceName,
  useParsedUserAgent,
} from "@/ui/device-info";
import { DeviceStatusBadge } from "@/ui/device-status-badge";
import { ClientCard, UserCard, UserCardBody } from "@/ui/entity-cards";
import { computeHumanReadableDateTimeStringFromUtc } from "@/utils/datetime";
import { ensureParametersAreUlids } from "@/utils/parameters";
import { deviceIdFromScope, scopeTokens } from "@/utils/scope";
import { userAgentSummary } from "@/utils/user-agent";

export const Route = createFileRoute("/_console/devices/user/$sessionId")({
  loader: async ({ context: { queryClient, credentials }, params }) => {
    ensureParametersAreUlids(params);
    const {
      data: { attributes },
    } = await queryClient.ensureQueryData(
      oauth2SessionQuery(credentials.serverName, params.sessionId),
    );

    queryClient.prefetchQuery(
      oauth2ClientQuery(credentials.serverName, attributes.client_id),
    );
    if (attributes.user_id) {
      queryClient.prefetchQuery(
        userQuery(credentials.serverName, attributes.user_id),
      );
    }
  },
  component: SessionDetailComponent,
  notFoundComponent: NotFoundComponent,
});

const detailsLabel = defineMessage({
  id: "pages.devices.user.details_label",
  defaultMessage: "Device details",
  description: "The accessible name of the device details panel",
});

function NotFoundComponent() {
  const { sessionId } = Route.useParams();
  const intl = useIntl();
  return (
    <Navigation.Details
      className="gap-4"
      aria-label={intl.formatMessage(detailsLabel)}
    >
      <CloseSidebar />

      <Alert
        type="critical"
        title={intl.formatMessage({
          id: "pages.devices.user.not_found.title",
          defaultMessage: "Device not found",
          description:
            "The title of the alert when a device could not be found",
        })}
      >
        <FormattedMessage
          id="pages.devices.user.not_found.description"
          defaultMessage="The requested device ({sessionId}) could not be found on {serverName}."
          description="The description of the alert when a device could not be found"
          values={{ sessionId, serverName: "E-Chat" }}
        />
      </Alert>

      <ButtonLink
        kind="secondary"
        size="md"
        to="/devices/user"
        Icon={ArrowLeftIcon}
      >
        <FormattedMessage {...messages.actionGoBack} />
      </ButtonLink>
    </Navigation.Details>
  );
}

const CloseSidebar: React.FC = () => {
  const intl = useIntl();
  const search = Route.useSearch();
  return (
    <div className="flex items-center justify-end">
      <Tooltip label={intl.formatMessage(messages.actionClose)}>
        <ButtonLink
          iconOnly
          to="/devices/user"
          search={search}
          kind="tertiary"
          size="md"
          Icon={CloseIcon}
        />
      </Tooltip>
    </div>
  );
};

function SessionDetailComponent() {
  const { credentials } = Route.useRouteContext();
  const { sessionId } = Route.useParams();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const {
    data: { data: session },
  } = useSuspenseQuery(oauth2SessionQuery(credentials.serverName, sessionId));

  const { data: clientData } = useQuery(
    oauth2ClientQuery(credentials.serverName, session.attributes.client_id),
  );

  const finished = !!session.attributes.finished_at;

  const { mutate: finishMutate, isPending: finishPending } = useMutation({
    mutationFn: () =>
      finishOauth2Session(queryClient, credentials.serverName, sessionId),
    onError: () => {
      toast.error(
        intl.formatMessage({
          id: "pages.devices.user.finish.error",
          defaultMessage: "Failed to remove the device",
          description: "Error toast when removing (signing out) a device fails",
        }),
      );
    },
    onSuccess: async (data) => {
      toast.success(
        intl.formatMessage({
          id: "pages.devices.user.finish.success",
          defaultMessage: "Device removed",
          description: "Success toast when a device was removed (signed out)",
        }),
      );
      queryClient.setQueryData(
        oauth2SessionQuery(credentials.serverName, sessionId).queryKey,
        data,
      );
      // Signing a device out changes more than this list: the applications list
      // filters on `filter[has-active-sessions]`, and the users list on
      // `filter[active-oauth2-client]` (plus the per-user device counts on the
      // user detail pane), so both can now show a stale device.
      queryClient.invalidateQueries({
        queryKey: ["mas", "oauth2-clients", credentials.serverName],
      });
      queryClient.invalidateQueries({
        queryKey: ["mas", "users", credentials.serverName],
      });
      await queryClient.invalidateQueries({
        queryKey: ["mas", "oauth2-sessions", credentials.serverName],
      });
      setOpen(false);
    },
  });

  const handleConfirm = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      finishMutate();
    },
    [finishMutate],
  );

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (finishPending) return;
      setOpen(next);
    },
    [finishPending],
  );

  const clientName =
    clientData?.data.attributes.client_name ?? session.attributes.client_id;

  const deviceId = deviceIdFromScope(session.attributes.scope);
  const userAgent = useParsedUserAgent(session.attributes.user_agent);
  const deviceName = useDeviceName({
    humanName: session.attributes.human_name,
    userAgent,
    fallback: clientName,
  });

  return (
    <Navigation.Details aria-label={intl.formatMessage(detailsLabel)}>
      <CloseSidebar />

      <div className="flex flex-col gap-6">
        <DetailHeader
          icon={<DeviceTypeHero deviceType={userAgent?.deviceType} />}
          title={deviceName}
          subtitle={userAgentSummary(userAgent)}
          badges={
            <DeviceStatusBadge
              finishedAt={session.attributes.finished_at}
              lastActiveAt={session.attributes.last_active_at}
            />
          }
        />

        {session.attributes.user_id && (
          <UserCard
            serverName={credentials.serverName}
            userId={session.attributes.user_id}
          />
        )}

        <ClientCard
          serverName={credentials.serverName}
          clientId={session.attributes.client_id}
        />

        <Data.Grid>
          {deviceId && (
            <Data.Item>
              <Data.Title>
                <FormattedMessage
                  id="pages.devices.user.device_id_label"
                  defaultMessage="Device ID"
                  description="Label for the Matrix device ID of a device"
                />
              </Data.Title>
              <Data.Value className="break-all font-mono">
                {deviceId}
              </Data.Value>
            </Data.Item>
          )}

          <Data.Item>
            <Data.Title>
              <FormattedMessage
                id="pages.devices.user.created_at_label"
                defaultMessage="Signed in at"
                description="Label for the date a device first signed in"
              />
            </Data.Title>
            <Data.Value>
              {computeHumanReadableDateTimeStringFromUtc(
                session.attributes.created_at,
              )}
            </Data.Value>
          </Data.Item>

          {session.attributes.last_active_at && (
            <Data.Item>
              <Data.Title>
                <FormattedMessage
                  id="pages.devices.user.last_active_label"
                  defaultMessage="Last active"
                  description="Label for the last-active date of a device"
                />
              </Data.Title>
              <Data.Value>
                {computeHumanReadableDateTimeStringFromUtc(
                  session.attributes.last_active_at,
                )}
              </Data.Value>
            </Data.Item>
          )}

          {session.attributes.last_active_ip && (
            <Data.Item>
              <Data.Title>
                <FormattedMessage
                  id="pages.devices.user.last_active_ip_label"
                  defaultMessage="Last active IP"
                  description="Label for the last-active IP of a device"
                />
              </Data.Title>
              <Data.Value>{session.attributes.last_active_ip}</Data.Value>
            </Data.Item>
          )}

          {session.attributes.finished_at && (
            <Data.Item>
              <Data.Title>
                <FormattedMessage
                  id="pages.devices.user.finished_at_label"
                  defaultMessage="Signed out at"
                  description="Label for the sign-out date of a device"
                />
              </Data.Title>
              <Data.Value>
                {computeHumanReadableDateTimeStringFromUtc(
                  session.attributes.finished_at,
                )}
              </Data.Value>
            </Data.Item>
          )}
        </Data.Grid>

        <Disclosure
          summary={
            <FormattedMessage
              id="pages.devices.user.technical_details"
              defaultMessage="Technical details"
              description="Summary of the collapsed section with technical details (scope, user agent) of a device"
            />
          }
        >
          <Data.Grid>
            <Data.Item>
              <Data.Title>
                <FormattedMessage
                  id="pages.devices.user.scope_label"
                  defaultMessage="Scope"
                  description="Label for the scope field of a device"
                />
              </Data.Title>
              {scopeTokens(session.attributes.scope).map((token) => (
                <Data.Value key={token} className="break-all font-mono">
                  {token}
                </Data.Value>
              ))}
            </Data.Item>

            {session.attributes.user_agent && (
              <Data.Item>
                <Data.Title>
                  <FormattedMessage
                    id="pages.devices.user.user_agent_label"
                    defaultMessage="User agent"
                    description="Label for the user-agent of a device"
                  />
                </Data.Title>
                <Data.Value className="break-all">
                  {session.attributes.user_agent}
                </Data.Value>
              </Data.Item>
            )}
          </Data.Grid>
        </Disclosure>

        {!finished && (
          <Dialog.Root
            open={open}
            onOpenChange={onOpenChange}
            trigger={
              <Button
                type="button"
                size="md"
                kind="secondary"
                destructive
                Icon={DeleteIcon}
              >
                <FormattedMessage
                  id="pages.devices.user.finish.button"
                  defaultMessage="Remove device"
                  description="Button label to remove (sign out) a device"
                />
              </Button>
            }
          >
            <Dialog.Title>
              <FormattedMessage
                id="pages.devices.user.finish.title"
                defaultMessage="Remove this device?"
                description="Title of the confirmation dialog when removing (signing out) a device"
              />
            </Dialog.Title>
            <Dialog.Description>
              <FormattedMessage
                id="pages.devices.user.finish.description"
                defaultMessage="The user will be signed out of this device. Access and refresh tokens will be revoked immediately."
                description="Description of the confirmation dialog when signing out (finishing) a device"
              />
            </Dialog.Description>
            <div className="flex flex-col gap-2">
              {session.attributes.user_id && (
                <StaticEntityCard>
                  <UserCardBody
                    serverName={credentials.serverName}
                    userId={session.attributes.user_id}
                  />
                </StaticEntityCard>
              )}
              <StaticEntityCard>
                <DeviceInfo
                  humanName={session.attributes.human_name}
                  userAgent={session.attributes.user_agent}
                  deviceId={deviceId}
                  fallbackName={clientName}
                />
              </StaticEntityCard>
            </div>
            <Button
              type="button"
              kind="primary"
              destructive
              disabled={finishPending}
              onClick={handleConfirm}
              Icon={finishPending ? undefined : DeleteIcon}
            >
              {finishPending && <InlineSpinner />}
              <FormattedMessage
                id="pages.devices.user.finish.confirm"
                defaultMessage="Remove device"
                description="Confirmation button label for removing (signing out) a device"
              />
            </Button>
            <Dialog.Close asChild>
              <Button type="button" kind="tertiary" disabled={finishPending}>
                <FormattedMessage {...messages.actionCancel} />
              </Button>
            </Dialog.Close>
          </Dialog.Root>
        )}
      </div>
    </Navigation.Details>
  );
}
